import React from 'react';
import { Button } from '@quillbot/design-system';
import { makeStyles } from '@mui/styles';
import Link from 'next/link';
import { Add, DragHandle } from '@mui/icons-material';
import Carousel, { PagingDots } from 'nuka-carousel';
import { useSelector } from 'react-redux';
import { RootState } from '@Common/models/redux/store';
import Highlight from '../Highlight';

const data = [
  {
    icon: '../images/theme/commonImages/paraphraserPillar/important-quoting-icon.svg',
    title: 'Quoting a Source',
    text: `
    <Highlight>
      When something is quoted, it is repeated in the same way it originally appeared.</Highlight>
    Reading aloud from a book or a magazine would be considered quoting that book or
    magazine, because the words are being read in the exact way in which they were
    written.`,
    link: '/',
    btnText: 'Try the Paraphraser',
  },
  {
    icon: '../images/theme/commonImages/paraphraserPillar/important-summarizing-icon.svg',
    title: 'Summarizing a Source',
    text: `
    Another referential device commonly used is summarizing. A summary is a short synopsis of a source's main points.&nbsp;
    <Highlight>
    Unlike direct quotes, summaries convey a broader picture of the referenced material as a whole, rather than repeating specific details word for word.</Highlight>`,
    link: '/summarize',
    btnText: 'Try the Summarizer',
  },
];

const useStyles = makeStyles((theme) => ({
  root: {
    padding: '60px 15px 16px',
    maxWidth: 665,
    margin: '0 auto',
    [theme.breakpoints.down('sm')]: {
      padding: '52px 32px 16px',
    },
  },
  title: {
    fontSize: 24,
    fontWeight: 600,
    margin: 0,
  },
  text: {
    fontSize: 16,
  },
  mobileSlider: {
    '& .slider-slide': {
      '&:first-child': {
        zIndex: 2,
      },
    },
    '& .slider-control-bottomcenter': {
      bottom: '-32px !important',
      '& .paging-item button svg': {
        display: 'none',
      },
      '& .paging-item': {
        marginLeft: 6,
      },
      '& .paging-item button': {
        width: '8px',
        height: '8px',
        borderRadius: '100px',
        opacity: '1 !important',
        backgroundColor: `${theme.specialColors.CIRCULAR_PROGRESS}  !important`,
        padding: 0,
      },
      '& .paging-item.active button': {
        backgroundColor: `${theme.palette.primary.main} !important`,
        width: '18px',
        borderRadius: '50px',
      },
    },
  },
  cardsWrapper: {
    margin: '40px 0 44px',
    [theme.breakpoints.down('sm')]: {
      margin: '40px 0 56px',
    },
  },
  cardItemWrapper: {
    backgroundColor: theme.palette.background.default,
    borderRadius: 8,
    padding: '26px 26px 36px 26px',
    marginTop: 20,
    '&:first-child': {
      marginTop: 0,
    },
    [theme.breakpoints.down('sm')]: {
      marginTop: 0,
    },
  },
  card: {
    display: 'flex',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  icon: {
    marginRight: 32,
    [theme.breakpoints.down('sm')]: {
      marginRight: 0,
      marginBottom: 20,
    },
    '& img': {
      width: 70,
      [theme.breakpoints.down('sm')]: {
        width: 44,
      },
    },
  },
  cardContent: {},
  cardTitle: {
    fontSize: 16,
    fontWeight: 700,
    marginBottom: 16,
  },
  cardText: {
    fontSize: 16,
    '& highlight': {
      backgroundColor: theme.specialColors.STRUCT_DIFF_COLOR,
    },
  },
  btnWrapper: {
    textAlign: 'center',
    marginTop: 32,
  },
  cardBtn: {
    fontSize: 17,
    fontWeight: 600,
    lineHeight: '23.15px',
    borderRadius: '100px',
    minWidth: '222px',
    padding: '10px 26px',
  },
  subHeading: {
    fontSize: 20,
    fontWeight: 600,
    fontStyle: 'italic',
    marginBottom: 44,
    padding: '0 16px',
    textAlign: 'center',
  },
  equationWrapper: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 44,
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  equationCard: {
    width: 176,
    height: 109,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 40,
  },
  blueCard: {
    backgroundColor: '#6CB9FF',
  },
  yellowCard: {
    backgroundColor: '#FFCF50',
  },
  greenCard: {
    backgroundColor: '#61C774',
  },
  equationTitle: {
    fontSize: 20,
    fontWeight: 600,
    lineHeight: '15px',
  },
  equationSubtitle: {
    fontSize: 16,
    marginTop: 12,
    lineHeight: '12px',
  },
  equationSign: {
    fontSize: 40,
    margin: '0 16px',
    [theme.breakpoints.down('sm')]: {
      margin: '20px 0',
    },
  },
}));

function Importance() {
  const isMobile = useSelector((state: RootState) => state.common.platform.isMobile);
  const classes = useStyles();

  return (
    <div className={classes.root}>
      <h2 className={classes.title}>Why is Paraphrasing so Important?</h2>
      <div className={classes.text}>
        <p>
          Understanding the importance of paraphrasing comes down to one thing: easy
          communication.&nbsp;
          <Highlight>
            When a source is paraphrased, it usually becomes more straightforward because it is
            rephrased and broken down in a way that is more easily digestible.
          </Highlight>
        </p>
        <p>
          So, why is paraphrasing important? To truly answer this question, we must first understand
          some other forms of information referencing.
        </p>
      </div>
      {isMobile ? (
        <div className={classes.cardsWrapper}>
          <Carousel
            className={classes.mobileSlider}
            slideWidth={1}
            cellSpacing={16}
            renderCenterLeftControls={null}
            renderCenterRightControls={null}
            framePadding="0 -12px 32px"
          >
            {data.map((item) => (
              <ImportanceCard
                key={item.title}
                icon={item.icon}
                title={item.title}
                text={item.text}
                link={item.link}
                btnText={item.btnText}
              />
            ))}
          </Carousel>
        </div>
      ) : (
        <div className={classes.cardsWrapper}>
          {data.map((item) => (
            <ImportanceCard
              key={item.title}
              icon={item.icon}
              title={item.title}
              text={item.text}
              link={item.link}
              btnText={item.btnText}
            />
          ))}
        </div>
      )}
      <div className={classes.subHeading}>
        Paraphrasing combines the brevity of summarizing with the precision of quoting. A paraphrase
        shouldn’t be an exact imitation of its reference, but it should convey the original source’s
        intent in your own words.
      </div>
      <div className={classes.text}>
        Paraphrasing isn’t necessarily expected to be as brief as possible. While quotes have to be
        exact, and summaries are meant to be short, a paraphrase doesn’t need to be either. It just
        needs to be accurate.
      </div>
      <div className={classes.equationWrapper}>
        <div className={`${classes.equationCard} ${classes.blueCard}`}>
          <div className={classes.equationTitle}>Brevity</div>
          <div className={classes.equationSubtitle}>Summary</div>
        </div>
        <Add className={classes.equationSign} />
        <div className={`${classes.equationCard} ${classes.yellowCard}`}>
          <div className={classes.equationTitle}>Precision</div>
          <div className={classes.equationSubtitle}>Quote</div>
        </div>
        <DragHandle className={classes.equationSign} />
        <div className={`${classes.equationCard} ${classes.greenCard}`}>
          <div className={classes.equationTitle}>Accuracy</div>
          <div className={classes.equationSubtitle}>Paraphrase</div>
        </div>
      </div>
    </div>
  );
}

function ImportanceCard({ icon, title, text, link, btnText }: any) {
  const classes = useStyles();

  return (
    <div className={classes.cardItemWrapper}>
      <div className={classes.card}>
        <div className={classes.icon}>
          <img src={icon} alt={title} />
        </div>
        <div className={classes.cardContent}>
          <div className={classes.cardTitle}>{title}</div>
          <div className={classes.cardText} dangerouslySetInnerHTML={{ __html: text }} />
        </div>
      </div>
      <div className={classes.btnWrapper}>
        <Link href={link}>
          <Button variant="contained" color="primary" className={classes.cardBtn}>
            {btnText}
          </Button>
        </Link>
      </div>
    </div>
  );
}

export default Importance;
